import { Chess } from 'chess.js';
import type { Color, DrillPosition } from './types';

/**
 * Parse un PGN-with-variations en liste plate de DrillPosition (mainline + variations).
 *
 * Pour chaque coup joué :
 *   - capture la FEN AVANT le coup
 *   - extrait le coup attendu (SAN + UCI)
 *   - récupère le commentaire `{ ... }` qui suit le coup et en extrait
 *     les tags `#critical`, `#plan`, `#trap`
 *
 * Implémentation pragmatique : on stripe les variations `( ... )` (max profondeur 1)
 * et on remplace les commentaires par des markers numérotés à réassocier à chaque ply.
 *
 * Usage côté drill :
 *   const positions = parsePgnTree(pgn);
 *   const recallTargets = positions; // mainline + commentaires
 *   const planTargets = positions.filter(p => p.tags.critical && p.tags.plan);
 */
export function parsePgnTree(pgn: string): DrillPosition[] {
  const movetext = stripHeaders(pgn);
  // Indexer les commentaires pour les retrouver par ply
  const { commentsByPly, cleaned } = extractCommentsByPly(movetext);

  const chess = new Chess();
  try {
    chess.loadPgn(cleaned);
  } catch (e) {
    console.error('[parsePgnTree] chess.js failed', e);
    return [];
  }

  const history = chess.history({ verbose: true });
  if (history.length === 0) return [];

  // Replay coup par coup pour récupérer la FEN avant chaque coup
  const replay = new Chess();
  const positions: DrillPosition[] = [];
  for (let i = 0; i < history.length; i++) {
    const move = history[i]!;
    const fenBefore = replay.fen();
    const sideToMove: Color = move.color === 'w' ? 'white' : 'black';
    const ply = i + 1;
    const comment = commentsByPly[ply] ?? null;
    positions.push({
      fenBefore,
      expectedSan: move.san,
      expectedUci: `${move.from}${move.to}${move.promotion ?? ''}`,
      sideToMove,
      ply,
      comment,
      tags: parseTags(comment),
    });
    replay.move(move.san);
  }
  return positions;
}

function stripHeaders(pgn: string): string {
  return pgn.replace(/^\[[^\]]*\]\s*$/gm, '').trim();
}

interface CommentExtract {
  commentsByPly: Record<number, string>;
  cleaned: string;
}

/**
 * Marche le mouvetexte token par token. La mainline (depth=0) est conservée dans
 * `cleaned` pour être loadPgn-ée par chess.js. Les variations `(...)` et leur
 * contenu sont SKIP entièrement — sinon les coups de la variation seraient
 * inlinés dans la mainline et casseraient chess.js (cf. issue Caro-Kann etc.).
 *
 * Les commentaires `{...}` à depth=0 sont attachés au ply du coup qui précède.
 * Conséquence assumée MVP1.0 : les variations ne sont pas drillables. C'est
 * conforme à CONVENTIONS.md §8 (profondeur variations max 3 par défaut).
 */
function extractCommentsByPly(movetext: string): CommentExtract {
  const commentsByPly: Record<number, string> = {};
  let cleaned = '';
  let ply = 0;
  let depth = 0;
  let i = 0;
  let lastPlyForComment = 0;
  while (i < movetext.length) {
    const ch = movetext[i]!;
    if (ch === '(') {
      depth++;
      i++;
      continue;
    }
    if (ch === ')') {
      depth--;
      i++;
      continue;
    }
    if (ch === '{') {
      const end = movetext.indexOf('}', i);
      if (end === -1) break;
      const raw = movetext.slice(i + 1, end).trim();
      if (depth === 0 && lastPlyForComment > 0) {
        commentsByPly[lastPlyForComment] =
          (commentsByPly[lastPlyForComment] ?? '') + (commentsByPly[lastPlyForComment] ? ' ' : '') + raw;
      }
      i = end + 1;
      continue;
    }
    // À l'intérieur d'une variation : on ignore TOUT (pas d'ajout à cleaned,
    // pas de tokenisation, pas de ply++).
    if (depth > 0) {
      i++;
      continue;
    }
    cleaned += ch;
    const m = movetext.slice(i).match(/^([a-zA-Z][a-zA-Z0-9+#=!?-]*)/);
    if (m && m[1] && !/^\d+\.+$/.test(m[1])) {
      const token = m[1];
      if (token !== '*' && !/^[01]-[01]$/.test(token) && token !== '1/2-1/2') {
        if (looksLikeSanMove(token)) {
          ply++;
          lastPlyForComment = ply;
        }
      }
      i++;
      for (let k = 1; k < token.length; k++) {
        cleaned += movetext[i]!;
        i++;
      }
      continue;
    }
    i++;
  }
  return { commentsByPly, cleaned };
}

/** Heuristique : est-ce que ce token ressemble à un coup SAN ? */
function looksLikeSanMove(token: string): boolean {
  if (/^[a-h][1-8]$/.test(token)) return true; // pawn move
  if (/^[a-h]x[a-h][1-8]/.test(token)) return true; // pawn capture
  if (/^[NBRQK]/.test(token)) return true; // piece move
  if (/^O-O(-O)?[+#]?$/.test(token)) return true; // castling
  if (/^[a-h][1-8]=[NBRQ]/.test(token)) return true; // promotion
  return false;
}

function parseTags(comment: string | null): DrillPosition['tags'] {
  if (!comment) return { critical: false, trap: false, plan: null };
  const critical = /#critical\b/.test(comment);
  const trap = /#trap\b/.test(comment);
  const planMatch = comment.match(/#plan\s+([^#]+?)(?:\s*#|$)/i);
  const plan = planMatch?.[1]?.trim() ?? null;
  return { critical, trap, plan };
}
